/**
 * Hook: risk classification assignment for one entity (department, room, bed, equipment).
 * Returns assignment, effective criticality (with inheritance), save, and loading state.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  riskClassificationAssignmentService
} from '../services/riskClassificationAssignmentService';

export function useRiskClassificationAssignment(organizationId, entityType, entityId, structure, item) {
  const [assignment, setAssignment] = useState(null);
  const [effective, setEffective] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAssignment = useCallback(async () => {
    if (!organizationId || !entityType || !entityId) {
      setAssignment(null);
      setEffective(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [assign, eff] = await Promise.all([
        riskClassificationAssignmentService.getAssignment(organizationId, entityType, entityId),
        riskClassificationAssignmentService.getEffectiveCriticality(
          organizationId,
          entityType,
          entityId,
          structure ?? {},
          item ?? null
        )
      ]);
      setAssignment(assign);
      setEffective(eff);
    } catch (err) {
      console.error('[useRiskClassificationAssignment]', err);
      setError(err.message || 'Failed to load assignment');
      setAssignment(null);
      setEffective(null);
    } finally {
      setLoading(false);
    }
  }, [organizationId, entityType, entityId, structure, item]);

  useEffect(() => {
    fetchAssignment();
  }, [fetchAssignment]);

  const save = useCallback(async (data, userId) => {
    if (!organizationId || !entityType || !entityId || !userId) {
      throw new Error('organizationId, entityType, entityId and userId are required');
    }
    await riskClassificationAssignmentService.saveAssignment(
      organizationId,
      entityType,
      entityId,
      data,
      userId
    );
    await fetchAssignment();
  }, [organizationId, entityType, entityId, fetchAssignment]);

  const remove = useCallback(async () => {
    if (!organizationId || !entityType || !entityId) return;
    await riskClassificationAssignmentService.deleteAssignment(
      organizationId,
      entityType,
      entityId
    );
    await fetchAssignment();
  }, [organizationId, entityType, entityId, fetchAssignment]);

  return {
    assignment,
    effective,
    loading,
    error,
    save,
    remove,
    refetch: fetchAssignment
  };
}
